import React from "react";
import {useTranslation} from "react-i18next";
import {Grid2} from "@mui/material";
import {CartonGradeCatalogDTO, CartonGradeDTO} from "@/api/data-contracts";
import {useModelState} from "@/lib/modelstate";
import {useCustomGradeSchema} from "./schema";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import NumberInputField from "@/lib/components/inputfields/number-input-field";
import {RequestI18N} from "@/translations";
import CheckboxInputField from "@/lib/components/inputfields/checkbox-input-field";
import {EDGE_CRUSH_UNIT, GSM} from "@/constants";
import {Constants} from "@/api/constants";

interface CustomGradeFormProps {
    originalGrade?: CartonGradeCatalogDTO;
    selectedGrade?: CartonGradeCatalogDTO;
    showOriginalGrade?: boolean;
    onSubmit: (grade: CartonGradeCatalogDTO) => void;
    setFormRef?: (ref: { submit: () => void }) => void;
}

/** Returns the original value as helper text when showOriginalGrade is active and the value has changed. */
function originalValueHelperText<T>(
        showOriginalGrade: boolean,
        originalValue: T | undefined,
        currentValue: T | undefined
): T | undefined {
    return showOriginalGrade && originalValue != currentValue ? originalValue : undefined;
}

export const CartonGradeForm: React.FC<CustomGradeFormProps> = ({
    originalGrade,
    selectedGrade,
    showOriginalGrade = false,
    onSubmit,
    setFormRef
}) => {
    const { t } = useTranslation();
    const schema = useCustomGradeSchema();

    const { model, errors, onChange, validate } = useModelState<CartonGradeDTO, null>({
        initialState: selectedGrade && !selectedGrade.importedAt ? selectedGrade.values : ({} as CartonGradeDTO),
        schema,
        id: null
    });

    React.useEffect(() => {
        setTimeout(() => validate({ silent: true }), 100);
    }, [selectedGrade, validate]);

    const handleSubmit = React.useCallback(() => {
        if (validate()) {
            const customGrade: CartonGradeCatalogDTO = { values: model };
            onSubmit(customGrade);
        }
    }, [validate, model, onSubmit]);

    React.useEffect(() => {
        if (setFormRef) {
            setFormRef({ submit: handleSubmit });
        }
    }, [setFormRef, handleSubmit]);

    const org = originalGrade?.values;

    const wetStrengthHelperText = (): string | undefined => {
        if (!showOriginalGrade || org?.wetStrength === model.wetStrength) {
            return undefined;
        }
        return org?.wetStrength ? t(RequestI18N.grade.form.wetStrengthWasNF) : t(RequestI18N.grade.form.wetStrengthWasNotNF);
    };

    return (
        <Grid2 container direction="column" spacing={3} py={1}>
            <Grid2>
                <StringInputField
                        label={t(RequestI18N.grade.form.qualityGradeLabel)}
                    name="code"
                    value={model.code}
                    error={errors?.code}
                    onChange={onChange}
                    maxChars={Constants.CARTON_GRADE_CODE_LENGTH_MAX}
                        placeholder={t(RequestI18N.grade.form.qualityGradeExample)}
                        helperText={originalValueHelperText(showOriginalGrade, org?.code, model.code)}
                />
            </Grid2>

            <Grid2>
                <StringInputField
                        label={t(RequestI18N.grade.form.fluteLabel)}
                    name="flute"
                    value={model.flute}
                    onChange={onChange}
                    maxChars={Constants.CARTON_GRADE_FLUTE_LENGTH_MAX}
                    error={errors?.flute}
                        placeholder={t(RequestI18N.grade.form.fluteExample)}
                        helperText={originalValueHelperText(showOriginalGrade, org?.flute, model.flute)}
                />
            </Grid2>

            <Grid2>
                <StringInputField
                        label={t(RequestI18N.grade.form.outerLinerLabel)}
                    name="outerLiner"
                    value={model.outerLiner}
                    error={errors?.outerLiner}
                    onChange={onChange}
                    maxChars={Constants.CARTON_GRADE_OUTER_LINER_LENGTH_MAX}
                        placeholder={t(RequestI18N.grade.form.outerLinerExample)}
                        helperText={originalValueHelperText(showOriginalGrade, org?.outerLiner, model.outerLiner)}
                />
            </Grid2>

            <Grid2>
                <StringInputField
                        label={t(RequestI18N.grade.form.fluteOneLabel)}
                    name="fluteOne"
                    value={model.fluteOne}
                    error={errors?.fluteOne}
                    onChange={onChange}
                    maxChars={Constants.CARTON_GRADE_FLUTE1_LENGTH_MAX}
                        placeholder={t(RequestI18N.grade.form.fluteOneExample)}
                        helperText={originalValueHelperText(showOriginalGrade, org?.fluteOne, model.fluteOne)}
                />
            </Grid2>

            <Grid2>
                <StringInputField
                        label={t(RequestI18N.grade.form.linerOneLabel)}
                    name="linerOne"
                    value={model.linerOne}
                    onChange={onChange}
                    maxChars={Constants.CARTON_GRADE_LINER1_LENGTH_MAX}
                    error={errors?.linerOne}
                        placeholder={t(RequestI18N.grade.form.linerOneExample)}
                        helperText={originalValueHelperText(showOriginalGrade, org?.linerOne, model.linerOne)}
                />
            </Grid2>

            <Grid2>
                <StringInputField
                        label={t(RequestI18N.grade.form.fluteTwoLabel)}
                    name="fluteTwo"
                    value={model.fluteTwo}
                    error={errors?.fluteTwo}
                    onChange={onChange}
                    maxChars={Constants.CARTON_GRADE_FLUTE2_LENGTH_MAX}
                        placeholder={t(RequestI18N.grade.form.fluteTwoExample)}
                        helperText={originalValueHelperText(showOriginalGrade, org?.fluteTwo, model.fluteTwo)}
                />
            </Grid2>

            <Grid2>
                <StringInputField
                        label={t(RequestI18N.grade.form.linerTwoLabel)}
                    name="linerTwo"
                    value={model.linerTwo}
                    error={errors?.linerTwo}
                    onChange={onChange}
                    maxChars={Constants.CARTON_GRADE_LINER2_LENGTH_MAX}
                        placeholder={t(RequestI18N.grade.form.linerTwoExample)}
                        helperText={originalValueHelperText(showOriginalGrade, org?.linerTwo, model.linerTwo)}
                />
            </Grid2>

            <Grid2>
                <StringInputField
                        label={t(RequestI18N.grade.form.fluteThreeLabel)}
                    name="fluteThree"
                    error={errors?.fluteThree}
                    value={model.fluteThree}
                    onChange={onChange}
                    maxChars={Constants.CARTON_GRADE_FLUTE3_LENGTH_MAX}
                        placeholder={t(RequestI18N.grade.form.fluteThreeExample)}
                        helperText={originalValueHelperText(showOriginalGrade, org?.fluteThree, model.fluteThree)}
                />
            </Grid2>

            <Grid2>
                <StringInputField
                        label={t(RequestI18N.grade.form.innerLinerLabel)}
                    name="innerLiner"
                    value={model.innerLiner}
                    error={errors?.innerLiner}
                    onChange={onChange}
                    maxChars={Constants.CARTON_GRADE_INNER_LINER_LENGTH_MAX}
                        placeholder={t(RequestI18N.grade.form.innerLinerExample)}
                        helperText={originalValueHelperText(showOriginalGrade, org?.innerLiner, model.innerLiner)}
                />
            </Grid2>

            <Grid2>
                <NumberInputField
                        label={t(RequestI18N.grade.form.weightLabel)}
                    name="weight"
                    value={model.weight}
                    onChange={onChange}
                    error={errors?.weight}
                    suffix={` ${GSM}`}
                        placeholder={t(RequestI18N.grade.form.weightExample)}
                    min={Constants.CARTON_GRADE_WEIGHT_MIN}
                    max={Constants.CARTON_GRADE_WEIGHT_MAX}
                    decimalScale={2}
                        helperText={originalValueHelperText(showOriginalGrade, org?.weight, model.weight)}
                />
            </Grid2>

            <Grid2>
                <NumberInputField
                        label={t(RequestI18N.grade.form.edgeCrushTestLabel)}
                    name="edgeCrushTest"
                    value={model.edgeCrushTest}
                    onChange={onChange}
                    error={errors?.edgeCrushTest}
                    suffix={` ${EDGE_CRUSH_UNIT}`}
                        placeholder={t(RequestI18N.grade.form.edgeCrushTestExample)}
                    min={Constants.CARTON_GRADE_EDGE_CRUSH_TEST_MIN}
                    max={Constants.CARTON_GRADE_EDGE_CRUSH_TEST_MAX}
                    decimalScale={2}
                        helperText={originalValueHelperText(showOriginalGrade, org?.edgeCrushTest, model.edgeCrushTest)}
                />
            </Grid2>

            <Grid2>
                <CheckboxInputField
                        label={t(RequestI18N.grade.form.nfTypeLabel)}
                    name="wetStrength"
                    value={model.wetStrength}
                    onChange={onChange}
                    error={errors?.wetStrength}
                        helperText={wetStrengthHelperText()}
                />
            </Grid2>
        </Grid2>
    );
};
